import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { JobFileEntry } from '@jobos/shared';

import { apiClient } from '@/api/client';

// The job list's photo data layer.
//
// One endpoint feeds two surfaces from a single fetch: `photos` is the job's
// complete photo set — what the lightbox pages — and `thumbUrls` covers only the
// first `thumbLimit` of them, which is what the hover popover draws. The two are
// deliberately not the same length, so an index into `photos` is never an index
// into `thumbUrls`.

/** What the popover grid draws, and the default the server applies. */
export const JOB_PHOTO_THUMB_LIMIT = 6;

export interface JobPhotosResponse {
  /** Every photo on the job, newest first. */
  photos: JobFileEntry[];
  /** Presigned thumbnail URLs, keyed by file id — first `thumbLimit` only. */
  thumbUrls: Record<string, string>;
}

export const jobPhotoKeys = {
  all: ['jobPhotos'] as const,
  /**
   * Keyed by job, and by the limit as well: two callers asking for different
   * numbers of thumbnails get different `thumbUrls`, and a grid drawn from a
   * shorter answer would render blanks in slots it had already laid out.
   */
  job: (jobId: string, thumbLimit: number) => [...jobPhotoKeys.all, jobId, thumbLimit] as const,
};

// The backend presigns for 15 minutes; going stale at 10 keeps a cached URL
// from being handed out with only seconds left on it. Same reasoning and the
// same numbers as useJobFileUrl — a job list is a long-lived page and must not
// re-hover into expired thumbnails.
const URL_STALE_MS = 10 * 60 * 1000;
const URL_GC_MS = 12 * 60 * 1000;

function requestJobPhotos(jobId: string, thumbLimit: number): Promise<JobPhotosResponse> {
  return apiClient.get<JobPhotosResponse>(`/jobs/${jobId}/files/photos`, {
    params: { thumbLimit },
  });
}

/**
 * A job's photos, through the same cache entry the hook fills.
 *
 * The click that opens the viewer can land before the hover's fetch has
 * resolved. Going through `fetchQuery` rather than the API directly means that
 * click joins the request already in flight instead of starting a second one,
 * and a job whose photos are already cached opens with no request at all.
 */
export function fetchJobPhotos(
  queryClient: QueryClient,
  jobId: string,
  thumbLimit: number = JOB_PHOTO_THUMB_LIMIT,
): Promise<JobPhotosResponse> {
  return queryClient.fetchQuery({
    queryKey: jobPhotoKeys.job(jobId, thumbLimit),
    queryFn: () => requestJobPhotos(jobId, thumbLimit),
    staleTime: URL_STALE_MS,
    gcTime: URL_GC_MS,
  });
}

/**
 * @param options.enabled Gate on top of the id check. The job list holds a row's
 * request until the pointer says the user is interested in that row — one
 * request per visible job on mount is exactly what this column must not cost.
 */
export function useJobPhotos(
  jobId: string | undefined,
  options: { enabled?: boolean; thumbLimit?: number } = {},
) {
  const thumbLimit = options.thumbLimit ?? JOB_PHOTO_THUMB_LIMIT;
  return useQuery({
    queryKey: jobPhotoKeys.job(jobId!, thumbLimit),
    queryFn: () => requestJobPhotos(jobId!, thumbLimit),
    enabled: !!jobId && (options.enabled ?? true),
    staleTime: URL_STALE_MS,
    gcTime: URL_GC_MS,
  });
}
